import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { viewElement } from '@hotjar/tracking';
import { useNotify } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { ENGAGE_INSIGHTS } from '@hotjar/webapp/constants/routes';
import {
  acceptUserTC,
  getEngageAccountAdmin,
  getEngageLoginStatus,
  getEngageUserStatus,
} from '../../domains/engage/api/api';

type PPUserStatus =
  | 'FIRST_ACTIVATION_TC_NEEDED'
  | 'FIRST_ACTIVATION_PP_NOT_ACTIVATED'
  | 'REDIRECT_TO_PP'
  | 'PP_ACTIVATED_FOR_ORG_TC_NEEDED';

export const useEngageButton = (
  hasEngageAccessTeam: boolean,
  hasEngageCreateTeam: boolean,
  hasEngageInsights: boolean,
): {
  onClick: (accountId: number, orgId: number, siteId: number) => void;
  getStatus: (orgId: number) => Promise<PPUserStatus>;
  acceptTermsAndConditions: (accountId: number, orgId: number, siteId: number) => Promise<void>;
  isTCModalOpen: boolean;
  isNeedActivationModalOpen: boolean;
  setTCModalState: (state: boolean) => void;
  setNeedActivationModalState: (state: boolean) => void;
  accountAdminName?: string;
} => {
  const [isTCModalOpen, setTCModalState] = useState(false);
  const [isNeedActivationModalOpen, setNeedActivationModalState] = useState(false);
  const [accountAdminName, setAccountAdminName] = useState<string>();
  const { showToast } = useNotify();
  const { t } = useTranslationWithPrefix('auth:engage');
  const navigate = useNavigate();

  const showUnhandledError = () => showToast({ kind: 'danger', text: t('unhandled-error') });

  const redirect = (accountId: number, orgId: number, siteId: number) => {
    getEngageLoginStatus(orgId, siteId)
      .then((response) => {
        if (response?.status === 'ok') {
          viewElement({
            name: 'engageLogin',
            uiPosition: 'Engage Button',
            groupData: { account_id: accountId, organization_id: orgId, site_id: siteId },
            data: {},
          });
          if (hasEngageInsights) {
            navigate(ENGAGE_INSIGHTS.BASE.path);
          } else {
            window.open('/engage/', '_blank');
          }
        }
      })
      .catch(showUnhandledError);
  };

  const getStatus = (orgId: number): Promise<PPUserStatus> => {
    return getEngageUserStatus(orgId).then((data) => {
      if (!data.userExists && !data.teamExists) {
        if (hasEngageCreateTeam) {
          // Account-owner: First activation
          return 'FIRST_ACTIVATION_TC_NEEDED';
        }
        // Non account-owner: PP not activated for Organization
        return 'FIRST_ACTIVATION_PP_NOT_ACTIVATED';
      }

      if (data.userExists && !data.teamExists) {
        if (hasEngageCreateTeam) {
          // Account-owner: Activation in another Org
          return 'FIRST_ACTIVATION_TC_NEEDED';
        }

        // Non account-owner: Activation in another Org
        return 'FIRST_ACTIVATION_PP_NOT_ACTIVATED';
      }

      if (!data.userExists && data.teamExists) {
        // Non account-owner: PP activated for Organization
        return 'PP_ACTIVATED_FOR_ORG_TC_NEEDED';
      }

      // Subsequent Login, Account Owner & Regular User
      return 'REDIRECT_TO_PP';
    });
  };

  const onClick = (accountId: number, orgId: number, siteId: number) => {
    getStatus(orgId)
      .then((result) => {
        switch (result) {
          case 'FIRST_ACTIVATION_TC_NEEDED':
            setTCModalState(true);
            break;
          case 'FIRST_ACTIVATION_PP_NOT_ACTIVATED':
            getEngageAccountAdmin(accountId)
              .then((response) => {
                setAccountAdminName(response.accountAdminName);
                setNeedActivationModalState(true);
              })
              .catch(showUnhandledError);
            break;
          case 'REDIRECT_TO_PP':
            redirect(accountId, orgId, siteId);
            break;
          case 'PP_ACTIVATED_FOR_ORG_TC_NEEDED':
            setTCModalState(true);
            break;
          default:
            break;
        }
      })
      .catch(showUnhandledError);
  };

  const acceptTermsAndConditions = (
    accountId: number,
    orgId: number,
    siteId: number,
  ): Promise<void> => {
    viewElement({
      name: 'engageTCaccepted',
      uiPosition: 'T&C Modal',
      groupData: { account_id: accountId, organization_id: orgId, site_id: siteId },
      data: {},
    });
    return acceptUserTC()
      .then(() => {
        setTCModalState(false);
        redirect(accountId, orgId, siteId);
      })
      .catch(showUnhandledError);
  };

  return {
    onClick,
    getStatus,
    acceptTermsAndConditions,
    isTCModalOpen,
    isNeedActivationModalOpen,
    setTCModalState,
    setNeedActivationModalState,
    accountAdminName,
  };
};
